const handleResponse = async (response) => {
  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.message || response.statusText);
    } else {
      // HTML response (likely ngrok warning page or error page)
      const text = await response.text();
      if (text.includes('<!DOCTYPE') || text.includes('<html')) {
        throw new Error('Received HTML instead of JSON. This might be the ngrok warning page. Please click "Visit Site" on the ngrok page first.');
      }
      throw new Error(`Server returned ${response.status}: ${response.statusText}`);
    }
  }
  
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    if (text.includes('<!DOCTYPE') || text.includes('<html')) {
      throw new Error('Received HTML instead of JSON. This might be the ngrok warning page. Please click "Visit Site" on the ngrok page first.');
    }
    throw new Error('Server did not return JSON');
  }
  
  return response.json();
};

export const fetchMatchingRules = async (apiBaseUrl, pageUrl) => {
  // apiBaseUrl should be like 'https://domain.com/api/v1'
  // We need to append '/assist/rules' to it
  const url = new URL('assist/rules', apiBaseUrl.endsWith('/') ? apiBaseUrl : apiBaseUrl + '/');
  url.searchParams.set('pageUrl', pageUrl);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true', // Bypass ngrok warning page
    },
  });
  return handleResponse(response);
};

export const runAssistRule = async (apiBaseUrl, ruleId, payload) => {
  // apiBaseUrl should be like 'https://domain.com/api/v1'
  // We need to append '/assist/rules/{id}/execute' to it
  const url = new URL(`assist/rules/${ruleId}/execute`, apiBaseUrl.endsWith('/') ? apiBaseUrl : apiBaseUrl + '/');
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true', // Bypass ngrok warning page
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

export const sendActionChat = async (apiBaseUrl, payload) => {
  const url = new URL('actions/chat', apiBaseUrl.endsWith('/') ? apiBaseUrl : apiBaseUrl + '/');
  const controller = new AbortController();
  try {
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    return handleResponse(response);
  } catch (err) {
    throw err;
  }
};

/**
 * Stream action chat via SSE. Calls onStatus/onThinking as events arrive,
 * returns the final ActionChatResponse when the stream completes.
 */
export const sendActionChatStream = async (apiBaseUrl, payload, onStatus, onThinking) => {
  const url = new URL('actions/chat/stream', apiBaseUrl.endsWith('/') ? apiBaseUrl : apiBaseUrl + '/');
  const controller = new AbortController();
  // No fixed timeout for SSE streams — the stream sends events continuously
  // and deep searches can legitimately run for 30+ minutes. We use an inactivity
  // timeout instead: if no data arrives for 5 minutes, assume the connection is dead.
  let inactivityTimeout = null;
  const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes with no data = dead
  const resetInactivityTimer = () => {
    if (inactivityTimeout) clearTimeout(inactivityTimeout);
    inactivityTimeout = setTimeout(() => controller.abort(), INACTIVITY_MS);
  };

  try {
    resetInactivityTimer();
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (!response.ok) {
      const text = await response.text();
      if (text.includes('<!DOCTYPE') || text.includes('<html')) {
        throw new Error('Received HTML instead of JSON. Please click "Visit Site" on the ngrok page first.');
      }
      throw new Error(`Server returned ${response.status}: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      resetInactivityTimer(); // got data — reset the inactivity timer

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep incomplete last line

      let eventName = null;
      let dataLines = [];

      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventName = line.substring(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.substring(5));
        } else if (line.trim() === '') {
          // End of event — process it
          if (eventName && dataLines.length > 0) {
            const eventData = dataLines.join('\n').trim();
            try {
              const parsed = JSON.parse(eventData);
              if (eventName === 'status') onStatus?.(parsed.text);
              else if (eventName === 'thinking') onThinking?.(parsed.text);
              else if (eventName === 'result') result = parsed;
              else if (eventName === 'error') throw new Error(parsed.message || 'Stream error');
            } catch (parseErr) {
              if (eventName === 'error') throw parseErr;
              console.warn('Failed to parse SSE event:', eventName, eventData, parseErr);
            }
          }
          eventName = null;
          dataLines = [];
        }
      }
    }

    if (!result) throw new Error('Stream ended without a result');
    return result;
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('Connection lost — no data received for 5 minutes. The search may still be running on the server.');
    }
    throw err;
  } finally {
    if (inactivityTimeout) clearTimeout(inactivityTimeout);
  }
};

export const confirmAction = async (apiBaseUrl, payload) => {
  const url = new URL('actions/confirm', apiBaseUrl.endsWith('/') ? apiBaseUrl : apiBaseUrl + '/');
  const controller = new AbortController();
  try {
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    return handleResponse(response);
  } catch (err) {
    throw err;
  }
};

